import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../components/common/Button';
import Breadcrumbs from '../components/common/Breadcrumbs';
import Toast from '../components/common/Toast';
import { apiService } from '../services/api';
import { Competition } from '../types/interfaces';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

interface TeamMember {
  username: string;
  nickname?: string;
  first_name?: string;
  last_name?: string;
}

interface TeamRow {
  id: number;
  competition_id: number;
  name: string;
  lead_username: string;
  lead_nickname?: string;
  members: TeamMember[];
  member_count: number;
}

interface TeamDraft {
  name: string;
  lead_username: string;
  membersText: string;
}

interface ManageCompetitionTeamsModalProps {
  isOpen: boolean;
  onClose: () => void;
  competition: Competition;
}

const parseMembers = (raw: string): string[] => {
  const pieces = raw
    .split(/[\n,]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  return Array.from(new Set(pieces));
};

const formatMembers = (members: TeamMember[]): string => members.map((m) => m.username).join(', ');

const ManageCompetitionTeamsModal: React.FC<ManageCompetitionTeamsModalProps> = ({
  isOpen,
  onClose,
  competition,
}) => {
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, TeamDraft>>({});
  const [createName, setCreateName] = useState('');
  const [createLead, setCreateLead] = useState('');
  const [createMembers, setCreateMembers] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const isTeamCompetition = (competition?.competition_mode || 'individual') === 'team';

  const teamCountLabel = useMemo(() => {
    const count = teams.length;
    return `${count} team${count === 1 ? '' : 's'}`;
  }, [teams.length]);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiService.get(`/teams/competition/${competition.id}`);
      const payload = Array.isArray(response.data) ? response.data : [];
      setTeams(payload);
      const initialDrafts: Record<number, TeamDraft> = {};
      payload.forEach((team: TeamRow) => {
        initialDrafts[team.id] = {
          name: team.name,
          lead_username: team.lead_username,
          membersText: formatMembers(team.members || []),
        };
      });
      setDrafts(initialDrafts);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load teams';
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  }, [competition.id]);

  useEffect(() => {
    if (!isOpen) return;
    setCreateName('');
    setCreateLead('');
    setCreateMembers('');
    setError('');
    setToastMessage('');
    if (isTeamCompetition) {
      loadTeams();
    }
  }, [isOpen, isTeamCompetition, loadTeams]);

  useEscapeToClose(onClose, {
    enabled: isOpen && savingId === null,
    confirmIfDirty: () => (
      [createName, createLead, createMembers].some((value) => value.trim() !== '')
      || teams.some((team) => {
        const draft = drafts[team.id];
        return Boolean(draft) && (
          draft.name !== team.name
          || draft.lead_username !== team.lead_username
          || draft.membersText !== formatMembers(team.members || [])
        );
      })
    ),
  });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleCreateTeam = async () => {
    setError('');
    if (!createName.trim()) {
      setError('Team name is required');
      return;
    }
    if (!createLead.trim()) {
      setError('Lead username/email is required');
      return;
    }

    setSavingId(-1);
    try {
      await apiService.post('/teams/create', {
        competition_id: competition.id,
        name: createName.trim(),
        lead_username: createLead.trim(),
        members: parseMembers(createMembers),
      });
      setCreateName('');
      setCreateLead('');
      setCreateMembers('');
      setToastMessage('Team created');
      await loadTeams();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to create team';
      setError(String(msg));
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveTeam = async (teamId: number) => {
    const draft = drafts[teamId];
    if (!draft) return;
    setError('');
    setSavingId(teamId);
    try {
      await apiService.post(`/teams/update/${teamId}`, {
        name: draft.name.trim(),
        lead_username: draft.lead_username.trim(),
        members: parseMembers(draft.membersText),
      });
      setToastMessage('Team updated');
      await loadTeams();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to update team';
      setError(String(msg));
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteTeam = async (teamId: number) => {
    setError('');
    setSavingId(teamId);
    try {
      await apiService.delete(`/teams/${teamId}`);
      setToastMessage('Team deleted');
      await loadTeams();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to delete team';
      setError(String(msg));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <div className="register-modal-backdrop" onClick={handleBackdropClick}>
        <div className="register-modal manage-teams-modal" role="dialog" aria-modal="true">
          <div className="register-modal__header">
            <div className="manage-teams-modal__header-copy">
              <Breadcrumbs
                className="manage-teams-modal__breadcrumbs"
                items={[
                  { label: 'Dashboard', to: '/dashboard' },
                  { label: 'Competition', to: `/competition/${competition.id}` },
                  { label: 'Manage', to: `/manage/${competition.id}` },
                  { label: 'Teams' },
                ]}
              />
              <h2 className="register-modal__title">Manage Teams</h2>
            </div>
            <button
              type="button"
              className="register-modal__close"
              onClick={onClose}
              aria-label="Close modal"
              disabled={loading || savingId !== null}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="register-modal__form">
            {!isTeamCompetition ? (
              <div className="register-modal__error">
                This competition is not in team mode. Switch <strong>competition_mode</strong> to <strong>team</strong> first.
              </div>
            ) : (
              <>
                {error && (
                  <div className="register-modal__error" role="alert">
                    {error}
                  </div>
                )}

                <div className="manage-teams-section">
                  <h4>Create Team</h4>
                  <div className="manage-teams-grid">
                    <label className="input-wrapper">
                      <span className="input__label">Team Name</span>
                      <input
                        className="input input--default input--md"
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                        placeholder="e.g. Insight Ninjas"
                        disabled={savingId !== null}
                      />
                    </label>
                    <label className="input-wrapper">
                      <span className="input__label">Team Lead (username or email)</span>
                      <input
                        className="input input--default input--md"
                        value={createLead}
                        onChange={(e) => setCreateLead(e.target.value)}
                        placeholder="team lead"
                        disabled={savingId !== null}
                      />
                    </label>
                    <label className="input-wrapper">
                      <span className="input__label">Members (comma or newline separated)</span>
                      <textarea
                        className="input input--default input--md manage-teams-textarea"
                        value={createMembers}
                        onChange={(e) => setCreateMembers(e.target.value)}
                        placeholder="member1, member2, member3"
                        disabled={savingId !== null}
                      />
                    </label>
                  </div>
                  <div className="manage-teams-actions">
                    <Button
                      type="button"
                      variant="primary"
                      onClick={handleCreateTeam}
                      disabled={savingId !== null}
                    >
                      Create Team
                    </Button>
                  </div>
                </div>

                <div className="manage-teams-section">
                  <div className="manage-teams-section__header">
                    <h4>Teams</h4>
                    <span className="manage-teams-count">{teamCountLabel}</span>
                  </div>
                  {loading ? (
                    <p>Loading teams...</p>
                  ) : teams.length === 0 ? (
                    <p>No teams yet.</p>
                  ) : (
                    <div className="manage-teams-list">
                      {teams.map((team) => {
                        const draft = drafts[team.id] || {
                          name: team.name,
                          lead_username: team.lead_username,
                          membersText: formatMembers(team.members || []),
                        };
                        return (
                          <div className="manage-teams-card" key={team.id}>
                            <div className="manage-teams-card__header">
                              <strong>{team.name}</strong>
                              <span>{team.member_count} member{team.member_count === 1 ? '' : 's'}</span>
                            </div>
                            <div className="manage-teams-grid">
                              <label className="input-wrapper">
                                <span className="input__label">Team Name</span>
                                <input
                                  className="input input--default input--sm"
                                  value={draft.name}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [team.id]: { ...draft, name: e.target.value },
                                    }))
                                  }
                                  disabled={savingId !== null}
                                />
                              </label>
                              <label className="input-wrapper">
                                <span className="input__label">Lead Username</span>
                                <input
                                  className="input input--default input--sm"
                                  value={draft.lead_username}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [team.id]: { ...draft, lead_username: e.target.value },
                                    }))
                                  }
                                  disabled={savingId !== null}
                                />
                              </label>
                              <label className="input-wrapper">
                                <span className="input__label">Members</span>
                                <textarea
                                  className="input input--default input--sm manage-teams-textarea"
                                  value={draft.membersText}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [team.id]: { ...draft, membersText: e.target.value },
                                    }))
                                  }
                                  disabled={savingId !== null}
                                />
                              </label>
                            </div>
                            <div className="manage-teams-actions">
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={() => handleSaveTeam(team.id)}
                                disabled={savingId !== null}
                              >
                                Save Team
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleDeleteTeam(team.id)}
                                disabled={savingId !== null}
                              >
                                Delete Team
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            <div className="register-modal__actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={savingId !== null}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>

      {toastMessage && <Toast message={toastMessage} duration={2500} onClose={() => setToastMessage('')} />}
    </>
  );
};

export default ManageCompetitionTeamsModal;
